import { CONDITION_LABEL } from '../../../scripts/access-subtrack-resolver.mjs';

// Care plan constrained values — single source of truth.

/* ── Target definitions (measure key → defaults) ──────────────── */
export const TARGET_DEFS = {
  bp_systolic:  { label: 'Systolic BP',  unit: 'mmHg',   operator: '≤', value: 130,  guideline: 'ACC/AHA 2023' },
  bp_diastolic: { label: 'Diastolic BP', unit: 'mmHg',   operator: '≤', value: 80,   guideline: 'ACC/AHA 2023' },
  weight:       { label: 'Weight',       unit: 'kg',     operator: 'reduce_by_pct', value: null, display: '5-10% reduction' },
  bmi:          { label: 'BMI',          unit: 'kg/m²',  operator: '≤', value: 30 },
  hba1c:        { label: 'HbA1c',        unit: '%',      operator: '≤', value: 7.0,  guideline: 'ADA 2024' },
  ldl_c:        { label: 'LDL-C',        unit: 'mg/dL',  operator: '≤', value: 100 },
  egfr:         { label: 'eGFR',         unit: 'mL/min', operator: '≥', value: null },
  uacr:         { label: 'UACR',         unit: 'mg/g',   operator: '≤', value: null },
  steps_daily:  { label: 'Daily Steps',  unit: 'steps',  operator: '≥', value: 7500 },
};

/* ── Qualifying conditions ────────────────────────────────────── */
// Derived from the shared resolver — the ONE canonical vocabulary (restores the
// previously-missing 'Overweight'; kills the hand-kept drifted copy). Core set
// ONLY, deliberately: EXTENDED_ICD10 (CMS-only conditions) must never become
// care-plan-addable — do not "helpfully" union it in.
export const CONDITIONS = Object.values(CONDITION_LABEL);

/* ── Devices ──────────────────────────────────────────────────── */
// The product names, matching what WF1/WF15 write into care_plans.devices. 'Body Pro'
// (unversioned) is the string static S-58 exists to keep OUT of the data — offering it
// here let an operator author by hand exactly what the workflows are pinned never to write.
export const DEVICES = ['BPM Pro 2', 'Body Pro 2'];

/* ── Lifestyle focus areas ────────────────────────────────────── */
export const LIFESTYLE_FOCUS = [
  'exercise',
  'sodium-reduction',
  'nutrition',
  'sleep-hygiene',
  'stress-management',
];

/* ── Content curriculum tracks ────────────────────────────────── */
// Live catalog vocabulary (2026-07-23): heart-health = ASCVD secondary prevention,
// cholesterol = the lipid pillar. Retired tracks (sleep-health, motivation, medication,
// journey-milestones, onboarding) removed from the picker — existing plans carrying them
// still render via the raw slug fallback.
export const CONTENT_TRACKS = [
  'hypertension',
  'cholesterol',
  'heart-health',
  'kidney-health',
  'diabetes-care',
  'weight-management',
  'lifestyle-spine',
  'device-mastery',
];

/* ── Medication classes ───────────────────────────────────────── */
export const MEDICATION_CLASSES = [
  'ace-inhibitor',
  'arb',
  'ccb',
  'diuretic',
  'beta-blocker',
  'metformin',
  'statin',
  'glp1',
  'warfarin',
];

/* ── Shared enums (previously duplicated across components) ──── */
export const RISK_LEVELS   = ['standard', 'elevated', 'high', 'critical'];
export const PLAN_STATUSES = ['draft', 'active', 'archived'];

/* ── Guideline pathway labels (mirrors care_pathway_preset seed) ─ */
export const PATHWAY_META = {
  'htn':         { label: 'Hypertension',              guideline: 'ACC/AHA 2017' },
  'lipid':       { label: 'Dyslipidemia',              guideline: 'ACC/AHA Cholesterol 2018' },
  'obesity':     { label: 'Overweight / Obesity',      guideline: 'AACE Obesity Algorithm 2023' },
  'prediabetes': { label: 'Pre-diabetes',              guideline: 'ADA Standards of Care 2024' },
  't2d':         { label: 'Type 2 Diabetes',           guideline: 'ADA Standards of Care 2024' },
  'ckd':         { label: 'CKD Stage 3',               guideline: 'KDIGO 2021 BP / KDIGO 2024 CKD' },
  'ckd-3a':      { label: 'CKD Stage 3a',              guideline: 'KDIGO 2024 CKD' },
  'ckd-3b':      { label: 'CKD Stage 3b',              guideline: 'KDIGO 2024 CKD' },
  'ascvd':       { label: 'ASCVD (established)',       guideline: '2022 ACC ECDP / ACC-AHA 2018' },
  'ckm':         { label: 'CKM (Cardio-Kidney-Metabolic)', guideline: 'KDIGO 2024 CKM', meta: true },
  'ascvd-t2d':   { label: 'ASCVD + T2D',               guideline: 'ADA/ACC Secondary Prevention 2024', meta: true },
  'obesity-t2d': { label: 'Obesity + T2D',             guideline: 'AACE / ADA Dual-Indication 2024', meta: true },
};
